import type { TemplateTokenContext } from "@unbox/api";
import { useEffect, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { toastImageUploadFailed } from "@/components/message-composer/composer-toasts";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AutomatedMessageFields,
  type AutomatedMessageValue,
  hasMessageContent,
  type MessageDraft,
  type SendAtPart,
  useCommitMessageDraft,
  useSendAtControls,
} from "./automated-message-fields";

export type { AutomatedMessageValue } from "./automated-message-fields";

interface AutomatedMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue?: AutomatedMessageValue;
  submitLabel?: string;
  onSubmit: (value: AutomatedMessageValue) => void;
  // Controls which variable tokens the picker offers. Defaults to the creator
  // outreach set; safety policies pass "safety" to surface risk-related tokens.
  variableContext?: TemplateTokenContext;
  merchantResources?: boolean;
}

export function AutomatedMessageDialog({
  open,
  onOpenChange,
  initialValue,
  submitLabel = "Add message as step",
  onSubmit,
  variableContext = "creator-profile",
  merchantResources = true,
}: AutomatedMessageDialogProps) {
  /** Buffered, so closing without submitting discards the edit. */
  const [message, setMessage] = useState<MessageDraft>({ messageTemplateId: null, customMessage: null, image: null });
  const [sendAtValue, setSendAtValue] = useState<SendAtPart>({
    sendAtHour: initialValue?.sendAtHour ?? null,
    sendAtTimezone: initialValue?.sendAtTimezone ?? null,
  });
  const { commit, isPending } = useCommitMessageDraft();

  useEffect(() => {
    if (!open) return;
    setSendAtValue({
      sendAtHour: initialValue?.sendAtHour ?? null,
      sendAtTimezone: initialValue?.sendAtTimezone ?? null,
    });
  }, [open, initialValue]);

  const sendAt = useSendAtControls({
    initialValue,
    resetKey: open ? "open" : "closed",
    onChange: setSendAtValue,
  });

  const submit = async () => {
    try {
      const committed = await commit(message);
      onSubmit({ ...committed, ...sendAtValue });
      onOpenChange(false);
    } catch {
      toastImageUploadFailed();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[612px]">
        <DialogHeader>
          <DialogTitle>Automated Message</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-8 py-2">
          <AutomatedMessageFields
            initialValue={initialValue}
            resetKey={open ? "open" : "closed"}
            variableContext={variableContext}
            merchantResources={merchantResources}
            onChange={setMessage}
          />
        </div>

        <DialogFooter className="flex !justify-between items-center pt-6">
          {sendAt.toggle}
          <div className="flex gap-2 items-center">
            <Button color="secondary" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button size="sm" isDisabled={!hasMessageContent(message)} isLoading={isPending} onClick={submit}>
              {submitLabel}
            </Button>
          </div>
        </DialogFooter>

        {sendAt.selects}
      </DialogContent>
    </Dialog>
  );
}
